import React from "react";
import CustomImage from "./CustomImage";
import PriceDisplay from "./PriceDisplay";
import BeicaSaKutijama from '../images/BeicaSaKutijama.png';
import { PET_ACCESSORIES, getAccessoryDescription, getAccessoryLabel, getPetCount } from './petAccessoryUtils';
import { getOrderLeadDays, formatLeadDates, formatStoredDateRange } from './deliveryEstimate';
import { countryName } from './countries';
import { useLanguage } from '../i18n/translations';


export default function ThankYouForOrdering({ apiData, productsData, currency, country, translate }) {
  const { language } = useLanguage();

  const OrderedProducts = React.useMemo(() => {
    if (!apiData || !apiData.orderedItemsDetails) return null;

    let itemsDetails;
    try {
      itemsDetails = typeof apiData.orderedItemsDetails === 'string'
        ? JSON.parse(apiData.orderedItemsDetails)
        : apiData.orderedItemsDetails;
    } catch (error) {
      return null;
    }

    let customDataList = [];
    try {
      customDataList = apiData.customProductData
        ? (typeof apiData.customProductData === 'string'
            ? JSON.parse(apiData.customProductData)
            : apiData.customProductData)
        : [];
    } catch (_) {}

    return itemsDetails.map((product, index) => {
      const productDetails = product.product_details || product;

      const itemKey = product.cartKey ?? String(product.id);
      const customData = customDataList.find(cd => {
        const cdKey = cd.cartKey ?? String(cd.productId);
        return cdKey === itemKey;
      }) || null;

      const selectedAccessories = customData?.accessories || [];

      return (
        <div className="flex gap-4 py-4 border-b border-darkerSkin last:border-0" key={index}>
          <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-darkerSkin/30">
            <CustomImage
              src={productDetails.imageUrl}
              alt={productDetails.imageAlt || 'Product image'}
              className="w-20 h-20 object-cover"
            />
          </div>
          <div className="flex flex-col justify-center flex-1 min-w-0">
            <span className="text-sm font-semibold text-gray-800 leading-snug">
              {productDetails['name' + country] || productDetails.name || 'Product'}
              {product.is_set && product.set_label && (
                <span className="ml-2 text-xs font-normal text-brownSkin">({product.set_label})</span>
              )}
            </span>
            <div className="mt-1">
              <PriceDisplay
                product={{
                  [apiData.currency]: product.is_set ? product.price : (productDetails[apiData.currency] || product.price || '0'),
                  [`discounted_${apiData.currency}`]: product.is_set ? null : productDetails[`discounted_${apiData.currency}`],
                  is_on_sale: product.is_set ? false : (productDetails.is_on_sale || false),
                  sale_discount_percentage: product.is_set ? 0 : (productDetails.sale_discount_percentage || 0)
                }}
                currency={apiData.currency}
                className="text-sm"
              />
            </div>
            <span className="text-xs text-gray-500 mt-0.5">{translate("Qty:")} {product.qty || '1'}</span>
            {customData && (customData.color || customData.secondPet || selectedAccessories.length > 0 || (customData.customAccessories && customData.customAccessories.length > 0) || customData.description) && (
              <div className="mt-2 pt-2 border-t border-darkerSkin/30 space-y-1">
                {customData.color && (
                  <div className="text-xs text-gray-600">
                    {translate("Color")}: <span className="font-medium capitalize">{customData.color}</span>
                  </div>
                )}
                {customData.secondPet && (
                  <div className="text-xs text-gray-600">
                    {translate("Number of pets")}: <span className="font-medium">{getPetCount(customData)}</span>
                  </div>
                )}
                {selectedAccessories.length > 0 && (
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">{translate("Accessories")}:</span>
                    <ul className="mt-0.5 ml-2 space-y-0.5">
                      {selectedAccessories.map(id => {
                        const acc = PET_ACCESSORIES.find(a => a.id === id);
                        return acc ? (
                          <li key={id}>{getAccessoryLabel(acc, language)} – {getAccessoryDescription(acc, customData?.priceProfile, language)}</li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                )}
                {customData.customAccessories && customData.customAccessories.length > 0 && (
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">{translate("Custom accessories")}:</span>
                    <ul className="mt-0.5 ml-2 space-y-0.5 list-disc list-inside">
                      {customData.customAccessories.map((text, i) => (
                        <li key={i}>{text}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {customData.description && (
                  <div className="text-xs text-gray-600">
                    {translate("Text")}: <span className="font-medium">{customData.description}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });
  }, [apiData, country, language]);


  const hasPersonalMug = React.useMemo(() => {
    if (!apiData?.orderedItemsDetails) return false;
    try {
      const items = typeof apiData.orderedItemsDetails === 'string'
        ? JSON.parse(apiData.orderedItemsDetails)
        : apiData.orderedItemsDetails;
      return items.some(item => {
        const path  = (item.product_details?.path  || '').toLowerCase();
        const nameBA = (item.product_details?.nameBA || '').toLowerCase();
        const nameRS = (item.product_details?.nameRS || '').toLowerCase();
        return path.includes('personalna-ljubimac-soljica') || nameBA.includes('ljubimac') || nameRS.includes('ljubimac');
      });
    } catch { return false; }
  }, [apiData]);

  const hasPersonalPlate = React.useMemo(() => {
    if (!apiData?.orderedItemsDetails) return false;
    try {
      const items = typeof apiData.orderedItemsDetails === 'string'
        ? JSON.parse(apiData.orderedItemsDetails)
        : apiData.orderedItemsDetails;
      return items.some(item => {
        const path  = (item.product_details?.path  || '').toLowerCase();
        const nameBA = (item.product_details?.nameBA || '').toLowerCase();
        const nameRS = (item.product_details?.nameRS || '').toLowerCase();
        return path.includes('personalni-tanjir') || nameBA.includes('personalni tanjir') || nameRS.includes('personalni tanjir');
      });
    } catch { return false; }
  }, [apiData]);

  const deliveryDates = React.useMemo(() => {
    // Prefer the estimate snapshotted onto the order at checkout (matches the email exactly).
    const stored = formatStoredDateRange(apiData?.delivery_estimate_start, apiData?.delivery_estimate_end);
    if (stored) return stored;

    // Legacy fallback for orders placed before the snapshot existed.
    const countryCode = apiData?.country || 'BA';
    let items = [];
    try {
      items = typeof apiData?.orderedItemsDetails === 'string'
        ? JSON.parse(apiData.orderedItemsDetails)
        : (apiData?.orderedItemsDetails || []);
    } catch { items = []; }

    const leadDays = getOrderLeadDays(items, countryCode, (item) => ({
      production_days_min: item.product_details?.production_days_min,
      production_days_max: item.product_details?.production_days_max,
      path: item.product_details?.path,
    }));
    return formatLeadDates(leadDays);
  }, [apiData]);

  const countryLabel = apiData?.country ? countryName(apiData.country, language, translate) : 'N/A';

  return (
    <div className="w-full font-sans">

      {/* Hero */}
      <div className="flex flex-col items-center justify-center pt-8 pb-12 px-4 text-center">
        <img src={BeicaSaKutijama} className="max-w-[280px] w-full" alt="Hvala" />
        <h1 className="font-semibold mt-6 text-4xl text-gray-800">{translate("Thank You")}!</h1>
        <p className="mt-2 text-gray-500 max-w-sm">
          {translate("You will receive an email once your order is confirmed.")}
        </p>
      </div>

      {/* Main content */}
      <div className="w-full px-4 sm:px-8 max-w-2xl mx-auto pb-20 space-y-6">

        {/* Order meta */}
        <div className="rounded-2xl bg-bgSkin p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-800 mb-4">{translate("Order details")}</h2>

          <Row label={translate("Order ID")} value={apiData?.orderId || 'N/A'} mono />

          <Row label={translate("Date")} value={
            apiData?.created_at
              ? new Date(apiData.created_at).toLocaleDateString('hr-BA', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                }).replace(',', '')
              : 'N/A'
          } />

          <Row label={translate("Payment method")} value={apiData?.paymentMethod || 'N/A'} />

          <div className="w-full h-px bg-darkerSkin" />

          <Row label={translate("Delivery")} value={`${apiData?.shippingCost || '0'} ${apiData?.currency}`} />
          <Row label={translate("Total")} value={`${(parseFloat(apiData?.total || 0) + parseFloat(apiData?.shippingCost || 0)).toFixed(2)} ${apiData?.currency}`} bold />

          <div className="flex items-start gap-2 pt-1">
            <svg className="w-4 h-4 text-brownSkin flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <span className="text-xs text-gray-600">
                {translate("Delivery within")} <span className="text-gray-800">{deliveryDates.start} – {deliveryDates.end}</span>
              </span>
              {hasPersonalMug && (
                <p className="text-xs text-gray-400 italic mt-0.5">
                  {translate("Due to handmade production and personalization, the mug takes about 4 weeks to make.")}
                </p>
              )}
              {hasPersonalPlate && (
                <p className="text-xs text-gray-400 italic mt-0.5">
                  {translate("Due to handmade production and personalization, the plate takes about 4 weeks to make.")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="rounded-2xl bg-bgSkin p-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">{translate("Ordered products")}</h2>
          {OrderedProducts}
        </div>

        {/* Delivery info */}
        <div className="rounded-2xl bg-bgSkin p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-800 mb-4">{translate("Delivery")}</h2>
          <Row label={translate("Name")} value={apiData?.name || `${apiData?.firstName || ''} ${apiData?.lastName || ''}`} />
          <Row label={translate("Email")} value={apiData?.email || 'N/A'} />
          <Row label={translate("Phone")} value={apiData?.phone || 'N/A'} />
          <Row label={translate("Country")} value={countryLabel} />
          <Row label={translate("City")} value={apiData?.city || 'N/A'} />
          <Row label={translate("Address")} value={apiData?.address || 'N/A'} />
        </div>

      </div>
    </div>
  );
}

function Row({ label, value, bold = false, mono = false }) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-sm text-right break-all ${bold ? 'font-semibold text-gray-800' : 'text-gray-700'} ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}
